/**
 * The conversations behind one chart point or table row, and the modal that
 * shows them (prototype `state/ConversationsContext.tsx`, which owned the
 * modal for the same reason: every module's charts drill into it, and eight
 * copies of the same state and dialog would be eight places to disagree).
 *
 * **The provider owns the modal.** It provides `DrillContext`, so every chart
 * point whose container calls `useDrill()` opens the point conversations
 * modal, and `OpenConversationsContext`, behind `useOpenConversations()`, for
 * the table rows (Competitors pages, Community voices and communities).
 *
 * **Nothing here persists**: the modal closes on navigation and a reload opens
 * nothing. The list is fetched page by page (server-side sort and paging); the
 * query is frozen when the modal opens, so the count matches what was clicked.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import {
  ConversationsModal,
  CONVERSATIONS_PAGE_SIZE,
  DEFAULT_CONVERSATIONS_VIEW,
  conversationsBadge,
} from '@deepdive-ui';
import type { ConversationRowView, ConversationsView } from '@deepdive-ui';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';

import type {
  ConversationsPage,
  ConversationsTarget,
  DashboardQuery,
  DrillRef,
} from '@/data-sources/contract';
import { isApiError } from '@/lib/errors';

import { useAppliedFilters } from '../data/AppliedFiltersContext';
import { useDataSource } from '../data/DataSourceContext';
import { usePageMeta } from '../data/PageContext';
import { canonicalQuery } from '../filters/canonicalQuery';
import { useReportScope, withReportScope } from '../filters/useReportScope';
import { DrillContext } from '../widgets/drill';
import { useListConversationsQuery } from './api/conversations.api';
import { usePostBreakdown } from './hooks';
import {
  presentConversationRow,
  presentConversationsHeading,
  type OpenConversationsRequest,
} from './present';
import { OpenConversationsContext } from './useOpenConversations';

type Session = {
  request: OpenConversationsRequest;
  target: ConversationsTarget;
  query: DashboardQuery;
};

function targetOf(request: OpenConversationsRequest): ConversationsTarget {
  switch (request.kind) {
    case 'point':
      return { kind: 'point', drill: request.drill };
    case 'page':
      return { kind: 'page', brand: request.brand, channel: request.channel };
    case 'community':
      return { kind: 'community', dimension: request.dimension, value: request.value };
  }
}

/** Opens a post's permalink in a new tab: the post is on somebody else's site. */
const openPost = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

export function ConversationsProvider({ children }: { children: ReactNode }) {
  const { source, bootstrap } = useDataSource();
  const options = bootstrap.filterOptions;
  const filters = useAppliedFilters();
  const { filtersType } = usePageMeta();
  // A custom report scopes View details to its campaign's posts, as it does its widgets.
  const dashboardId = useReportScope();

  /*
   * The session outlives `open` going false on purpose: `Dialog` fades out,
   * and a panel emptied the moment it was dismissed would blank out mid-fade.
   */
  const [session, setSession] = useState<Session | null>(null);
  const [open, setOpen] = useState(false);
  /* Sort and page — owned here so a `Breakdown ›` round trip keeps them. Reset on each opening. */
  const [view, setView] = useState<ConversationsView>(DEFAULT_CONVERSATIONS_VIEW);
  /* The conversation whose breakdown the card shows; kept after `‹` so the panel does not blank mid-swap. */
  const [breakdownRow, setBreakdownRow] = useState<ConversationRowView | null>(null);
  const [showingBreakdown, setShowingBreakdown] = useState(false);

  const openWith = useCallback((request: OpenConversationsRequest, query: DashboardQuery) => {
    setSession({ request, target: targetOf(request), query });
    setView(DEFAULT_CONVERSATIONS_VIEW);
    setShowingBreakdown(false);
    setBreakdownRow(null);
    setOpen(true);
  }, []);

  /* The modal closes on navigation, as the prototype's did. Derived during render, not in an effect. */
  const { pathname } = useLocation();
  const [path, setPath] = useState(pathname);
  if (path !== pathname) {
    setPath(pathname);
    if (open) setOpen(false);
  }

  /* A chart point: resolved under the page's canonical query at the moment of the click. */
  const drill = useCallback(
    (ref: DrillRef) =>
      openWith(
        { kind: 'point', drill: ref },
        withReportScope(canonicalQuery(filters, filtersType), dashboardId),
      ),
    [openWith, filters, filtersType, dashboardId],
  );

  /* ---------------------------------------------------------- list */

  const list = useListConversationsQuery(
    session
      ? {
          source,
          params: {
            target: session.target,
            query: session.query,
            page: view.page,
            pageSize: CONVERSATIONS_PAGE_SIZE,
            sort: view.sort,
          },
        }
      : skipToken,
  );

  /*
   * The last page shown for this session, so a sort or page change keeps the
   * rows on screen until the next page arrives instead of flashing empty — and
   * a new session never shows the previous target's rows. Updated during
   * render, guarded, which is React's pattern for state derived from props.
   */
  const [shown, setShown] = useState<{ session: Session; page: ConversationsPage } | null>(null);
  if (session && list.currentData && shown?.page !== list.currentData) {
    setShown({ session, page: list.currentData });
  }
  const page = shown && shown.session === session ? shown.page : undefined;

  const heading = useMemo(
    () => (session ? presentConversationsHeading(session.request, options) : undefined),
    [session, options],
  );
  const rows = useMemo(
    () => page?.items.map((row) => presentConversationRow(row, options)),
    [page, options],
  );

  const listView = list.isError
    ? {
        rows: [],
        total: 0,
        emptyLabel:
          isApiError(list.error) && list.error.kind === 'notConnected'
            ? 'Live data for these conversations is not connected yet.'
            : 'Couldn’t load these conversations.',
      }
    : page && rows
      ? { rows, total: page.total }
      : undefined;

  /* ----------------------------------------------------- breakdown */

  const breakdownView = usePostBreakdown(breakdownRow?.id ?? null, 'conversation');

  const onViewConversation = useCallback(
    (conversation: ConversationRowView) => {
      const url = page?.items.find((item) => item.id === conversation.id)?.url;
      if (url) openPost(url);
    },
    [page],
  );

  const onBreakdown = useCallback((conversation: ConversationRowView) => {
    setBreakdownRow(conversation);
    setShowingBreakdown(true);
  }, []);

  return (
    <OpenConversationsContext.Provider value={openWith}>
      <DrillContext.Provider value={drill}>
        {children}
        <ConversationsModal
          open={open}
          onClose={() => setOpen(false)}
          list={listView}
          title={heading?.title ?? ''}
          badge={conversationsBadge(page?.total ?? 0, heading?.subject ?? '')}
          label={heading?.label ?? ''}
          helpText={heading?.helpText}
          helpSupportingText={heading?.helpSupportingText}
          view={view}
          onViewChange={setView}
          onViewConversation={onViewConversation}
          onBreakdown={onBreakdown}
          breakdown={{
            showing: showingBreakdown,
            name: breakdownRow?.text,
            view: breakdownView,
            onBack: () => setShowingBreakdown(false),
          }}
        />
      </DrillContext.Provider>
    </OpenConversationsContext.Provider>
  );
}
